import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { AnimatedNumber } from '../AnimatedNumber';
import { duration } from '../arena-ui';
import { capsuleTimerPaths } from './capsuleTimer';
import { percentLabel } from './parts';
import type { CoinView } from './model';
import { previewStatus } from './previewModel';
import { predictionState } from './predictionState';

export function ResultTag({
  coin,
  now,
  layout = 'rail',
  showNote = false,
}: {
  coin: CoinView;
  now: number;
  layout?: 'rail' | 'card' | 'explore';
  showNote?: boolean;
}) {
  const status = previewStatus(coin, now);
  const prediction = predictionState(coin, now);
  const note = layout === 'explore' || showNote ? coin.note : null;
  const bought =
    layout === 'explore' &&
    !!coin.choice &&
    prediction.kind === 'bought' &&
    !note;
  const active =
    !note &&
    (bought ||
      (status.kind === 'active' &&
        (layout !== 'card' || now >= coin.closesAt)));
  const timer = bought ? prediction : status;
  const body = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(
    layout === 'card' ? 140 : layout === 'explore' ? 380 : 229,
  );
  const clock = useRef<HTMLSpanElement>(null);
  const [clockWidth, setClockWidth] = useState(95);
  useLayoutEffect(() => {
    const node = body.current;
    if (!node) return;
    const measure = () =>
      setWidth(Number.parseFloat(getComputedStyle(node).width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    const node = clock.current;
    if (!node) return;
    const measure = () =>
      setClockWidth(Number.parseFloat(getComputedStyle(node).width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [active]);
  // Re-anchor when evidence arrives, but never when Run changes to Die.
  const timing =
    coin.id +
    ':' +
    coin.closesAt +
    ':' +
    coin.settlesAt +
    ':' +
    active +
    ':' +
    bought;
  const started = useRef({ timing, elapsed: timer.elapsed });
  if (started.current.timing !== timing)
    started.current = { timing, elapsed: timer.elapsed };
  const tone = note
    ? 'flat'
    : bought
      ? 'waiting'
      : status.choice === 'SURVIVES'
        ? 'up'
        : status.choice === 'DIES'
          ? 'down'
          : 'flat';
  const label =
    note ??
    (bought
      ? 'Bought'
      : status.choice === 'SURVIVES'
        ? 'RUN!'
        : status.choice === 'DIES'
          ? 'DIE!'
          : status.kind === 'pending'
            ? 'Verifying result'
            : 'No USD baseline');
  const paths = capsuleTimerPaths(width, 46, clockWidth);
  return (
    <div
      className={
        'preview-result-tag is-' +
        tone +
        (active ? ' is-active' : '') +
        (layout === 'card' ? ' is-card' : '')
      }
      data-result-state={
        note
          ? 'unavailable'
          : bought
            ? 'bought'
            : (status.choice === 'SURVIVES'
                ? 'run-'
                : status.choice === 'DIES'
                  ? 'die-'
                  : '') + status.kind
      }
      style={
        {
          '--result-ms': timer.total + 'ms',
          '--result-delay': -started.current.elapsed + 'ms',
          '--result-elapsed': String(timer.elapsed / timer.total),
        } as CSSProperties
      }
    >
      <div ref={body} className="preview-result-body">
        <span className="preview-result-label">
          <b>{label}</b>
          {!note && !bought && status.choice && status.percent !== null && (
            <span>
              <AnimatedNumber value={percentLabel(status.percent)} />
            </span>
          )}
        </span>
        {active && (
          <svg
            className="preview-result-trace"
            viewBox={`0 0 ${width} 46`}
            aria-hidden="true"
            focusable="false"
          >
            {paths.map((d, i) => (
              <path key={'track-' + i} className="preview-result-track" d={d} />
            ))}
            {paths.map((d, i) => (
              <path
                key={timing + '-' + i}
                className="preview-result-burn"
                pathLength="100"
                d={d}
              />
            ))}
          </svg>
        )}
      </div>
      {active && (
        <span ref={clock} className="preview-result-clock">
          <span>{bought ? 'Prediction closes' : 'Results in'}</span>
          <AnimatedNumber value={duration(timer.remaining)} />
        </span>
      )}
      <span className="sr-only">
        {note
          ? 'No confirmed result yet.'
          : bought
            ? 'Your prediction is locked.'
            : status.kind === 'final'
              ? 'Final result.'
              : status.kind === 'active'
                ? 'Round active. Current status is provisional.'
                : 'No confirmed result yet.'}
      </span>
    </div>
  );
}
